import { API_BASE, requestApi } from "./api";

export type OrderStatus = "QUOTE_PENDING" | "QUOTE_RECEIVED" | "QUOTE_APPROVED" | "ON_HOLD" | "CANCELLED";
export type ConstructionStatus = "WAITING" | "IN_PROGRESS" | "COMPLETED";

export interface WorkOrder {
  id: string;
  code: string;
  memberId: string;
  memberName: string;
  memberPhone: string;
  clientName: string;
  title: string;
  workTypes: string[];
  detail: string;
  requestedPeople: number;
  requestedStartDate: string;
  requestedEndDate: string;
  address: string;
  addressDetail: string;
  fileNames: string[];
  fileIds: string[];
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteItem {
  id: string;
  tradeName: string;
  itemName: string;
  unitPrice: number;
  quantity: number;
  amount: number;
  note: string;
  displayOrder: number;
}

export interface Assignment {
  id: string;
  tradeName: string;
  workerCode: string;
  workerName: string;
  startDate: string;
  endDate: string;
}

export interface Quote {
  id: string;
  code: string;
  orderId: string;
  status: "DRAFT" | "SENT" | "APPROVED";
  totalAmount: number;
  managerName: string;
  totalWorkers: number;
  startDate: string;
  endDate: string;
  fileName: string;
  fileId: string;
  items: QuoteItem[];
  assignments: Assignment[];
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  viewedAt?: string | null;
}

export interface WorkOrderDetail { order: WorkOrder; quote: Quote | null; }

export interface PageResult<T> {
  items: T[];
  totalElements: number;
  page: number;
  size: number;
}

export interface ConstructionTrade extends Assignment {
  status: ConstructionStatus;
  updatedAt: string;
}

export interface Construction {
  id: string;
  code: string;
  orderId: string;
  quoteId: string;
  memberId: string;
  status: ConstructionStatus;
  startDate: string;
  endDate: string;
  trades: ConstructionTrade[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface ConstructionDetail { construction: Construction; order: WorkOrder; quote: Quote; }

export interface ConstructionNote {
  id: string; constructionId: string; tradeName: string; type: "ISSUE" | "MEMO";
  title: string; content: string; createdBy: string; createdAt: string;
}

export interface CompletionReport {
  id: string; constructionId: string; actualCompletedAt: string; workResult: string; requestScope: string;
  documentFileIds: string[]; documentFileNames: string[]; photoFileIds: string[]; photoFileNames: string[];
  status: "DRAFT" | "CONFIRMED"; createdBy: string; createdAt: string; updatedAt: string; confirmedAt: string | null;
}

export interface SupplementRequest {
  id: string; code: string; constructionId: string; memberId: string; type: "SUPPLEMENT" | "OBJECTION";
  title: string; content: string; fileIds: string[]; fileNames: string[]; status: "PENDING" | "RESOLVED";
  createdAt: string; updatedAt: string;
}

export interface ConstructionManagement {
  notes: ConstructionNote[];
  completionReport: CompletionReport | null;
  supplementRequests: SupplementRequest[];
}

export interface CreateOrderInput {
  title: string;
  workTypes: string[];
  detail: string;
  requestedPeople: number;
  requestedStartDate: string;
  requestedEndDate: string;
  address: string;
  addressDetail: string;
  fileNames: string[];
  fileIds: string[];
}

export interface SaveQuoteInput {
  managerName: string;
  totalWorkers: number;
  startDate: string;
  endDate: string;
  fileName: string;
  fileId: string;
  items: Array<{ tradeName: string; itemName: string; unitPrice: number; quantity: number; note: string; displayOrder: number }>;
  assignments: Array<{ tradeName: string; workerCode: string; workerName: string; startDate: string; endDate: string }>;
}

export const orderStatusLabel: Record<OrderStatus, string> = {
  QUOTE_PENDING: "견적대기",
  QUOTE_RECEIVED: "견적도착",
  QUOTE_APPROVED: "확정",
  ON_HOLD: "보류",
  CANCELLED: "취소",
};

export const constructionStatusLabel: Record<ConstructionStatus, string> = {
  WAITING: "대기",
  IN_PROGRESS: "진행중",
  COMPLETED: "완료",
};

export const adminOrderStatusLabel: Record<OrderStatus, string> = {
  QUOTE_PENDING: "발주요청",
  QUOTE_RECEIVED: "견적작성완료",
  QUOTE_APPROVED: "시공확정",
  ON_HOLD: "보류",
  CANCELLED: "취소",
};

export function createOrder(input: CreateOrderInput) {
  return requestApi<WorkOrderDetail>("/orders", { method: "POST", body: JSON.stringify(input) });
}
export function getOrders(admin = false) { return requestApi<WorkOrderDetail[]>(admin ? "/admin/orders" : "/orders"); }
export function getAdminOrdersPage(input: { code?: string; title?: string; client?: string; status?: string; page?: number; size?: number }) {
  const query = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => { if (value !== undefined && value !== "") query.set(key, String(value)); });
  return requestApi<PageResult<WorkOrderDetail>>(`/admin/orders/search?${query.toString()}`);
}
export function getOrder(code: string, admin = false) { return requestApi<WorkOrderDetail>(`${admin ? "/admin" : ""}/orders/${encodeURIComponent(code)}`); }
export function markQuoteViewed(code: string, version: string) { return requestApi<{ recorded: boolean }>(`/orders/${encodeURIComponent(code)}/quote-viewed`, { method: "POST", body: JSON.stringify({ version }) }); }
export async function downloadQuoteFile(code: string, originalName: string) {
  const response = await fetch(`${API_BASE}/orders/${encodeURIComponent(code)}/quote-file`, { credentials: "include" });
  if (!response.ok) throw new Error("견적서를 다운로드하지 못했습니다.");
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = originalName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function saveQuote(code: string, input: SaveQuoteInput) { return requestApi<WorkOrderDetail>(`/admin/orders/${encodeURIComponent(code)}/quote`, { method: "PUT", body: JSON.stringify(input) }); }
export function approveQuote(code: string) { return requestApi<ConstructionDetail>(`/orders/${encodeURIComponent(code)}/approve`, { method: "POST" }); }
export function getConstructions(admin = false) { return requestApi<ConstructionDetail[]>(admin ? "/admin/constructions" : "/constructions"); }
export function getAdminConstructionsPage(input: { code?: string; title?: string; client?: string; status?: string; page?: number; size?: number }) {
  const query = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => { if (value !== undefined && value !== "") query.set(key, String(value)); });
  return requestApi<PageResult<ConstructionDetail>>(`/admin/constructions/search?${query.toString()}`);
}
export function getConstruction(code: string, admin = false) { return requestApi<ConstructionDetail>(`${admin ? "/admin" : ""}/constructions/${encodeURIComponent(code)}`); }
export function updateConstructionTrade(code: string, tradeName: string, input: { status: ConstructionStatus; startDate?: string; endDate?: string }) {
  return requestApi<ConstructionDetail>(`/admin/constructions/${encodeURIComponent(code)}/trades/${encodeURIComponent(tradeName)}`, { method: "PATCH", body: JSON.stringify(input) });
}
export const getConstructionManagement = (code: string, admin = false) => requestApi<ConstructionManagement>(`${admin ? "/admin" : ""}/constructions/${encodeURIComponent(code)}/management`);
export const addConstructionNote = (code: string, input: { tradeName: string; type: "ISSUE" | "MEMO"; title: string; content: string }) => requestApi<ConstructionNote>(`/admin/constructions/${encodeURIComponent(code)}/notes`, { method: "POST", body: JSON.stringify(input) });
export const saveCompletionReport = (code: string, input: { actualCompletedAt: string; workResult: string; requestScope: string; documentFileIds: string[]; documentFileNames: string[]; photoFileIds: string[]; photoFileNames: string[] }) => requestApi<CompletionReport>(`/admin/constructions/${encodeURIComponent(code)}/completion-report`, { method: "PUT", body: JSON.stringify(input) });
export const confirmCompletionReport = (code: string) => requestApi<CompletionReport>(`/constructions/${encodeURIComponent(code)}/completion-report/confirm`, { method: "POST" });
export const createSupplementRequest = (code: string, input: { type: "SUPPLEMENT" | "OBJECTION"; title: string; content: string; fileIds: string[]; fileNames: string[] }) => requestApi<SupplementRequest>(`/constructions/${encodeURIComponent(code)}/supplements`, { method: "POST", body: JSON.stringify(input) });
